import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, extractError } from "../lib/api";
import { PageHeader, KpiTile, Pill } from "../components/Ui";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ClipboardCheck, Loader2, RefreshCw, Truck, X } from "lucide-react";
import { useWS } from "../context/WebSocketContext";
import { formatWeightKg } from "../lib/weightUnits";

const NONE = "__none__";
const REJECTION_FALLBACK = [
  { value: "DEFECTIVE", label: "Defective" },
  { value: "WRONG_SPECS", label: "Wrong specs" },
  { value: "DAMAGED_IN_TRANSIT", label: "Damaged in transit" },
  { value: "SHORTAGE", label: "Shortage" },
];

function typeTone(cat) {
  const u = String(cat || "").toUpperCase();
  if (u === "PACKING_MATERIAL") return "sage";
  if (u === "FIXED_ASSET") return "gold";
  if (u === "SPARES") return "warning";
  return "default";
}

function routeLabel(route, category) {
  if (route === "BAG_STOCK" || category === "PACKING_MATERIAL") return "Bag stock (store)";
  if (route === "FIXED_ASSET" || category === "FIXED_ASSET") return "Fixed asset register";
  return "Store inventory";
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function blankAdhocLine(item) {
  return {
    po_item_id: null,
    item_id: item.id,
    item_code: item.code,
    item_name: item.name,
    unit: item.unit,
    billed_qty: "",
    accepted_qty: "",
    rejected_qty: "0",
    rejection_reason: "",
    storage_location: "MAIN_STORE",
    serial_numbers: "",
    category: item.category || "",
  };
}

function blankLine(item) {
  const pending = Number(item.pending_qty ?? Math.max(0, Number(item.quantity || 0) - Number(item.received_qty || 0)));
  return {
    po_item_id: item.id,
    item_id: item.item_id,
    item_code: item.item_code,
    item_name: item.item_name,
    unit: item.unit,
    ordered_qty: item.quantity,
    pending_qty: pending,
    billed_qty: pending ? String(pending) : "",
    accepted_qty: pending ? String(pending) : "",
    rejected_qty: "0",
    rejection_reason: "",
    storage_location: "MAIN_STORE",
    serial_numbers: "",
    category: item.category || "",
  };
}

export default function StoreGRNIntake() {
  const { subscribe } = useWS();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [queue, setQueue] = useState([]);
  const [reasons, setReasons] = useState(REJECTION_FALLBACK);
  const [selectedId, setSelectedId] = useState(null);
  const [sheet, setSheet] = useState(null);
  const [lines, setLines] = useState([]);
  const [meta, setMeta] = useState({
    supplier_invoice_no: "",
    supplier_invoice_date: "",
    remarks: "",
  });
  const [poPick, setPoPick] = useState("");
  const [adhocPick, setAdhocPick] = useState("");

  const isAdhoc = useMemo(
    () => Boolean(sheet && !sheet.po_id && !sheet.purchase_order),
    [sheet],
  );

  const loadQueue = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/store/grn/queue");
      setQueue(data?.results || []);
      if (data?.rejection_reasons?.length) setReasons(data.rejection_reasons);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  useEffect(() => {
    const unsub = subscribe("store_grn", loadQueue);
    const unsubGate = subscribe("gate_pass", loadQueue);
    return () => {
      unsub?.();
      unsubGate?.();
    };
  }, [subscribe, loadQueue]);

  const openSheet = useCallback(async (row) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/store/grn/${row.gate_pass_id}`);
      setSheet(data);
      setSelectedId(row.gate_pass_id);
      const po = data?.purchase_order;
      setPoPick(po?.id ? String(po.id) : "");
      const items = po?.items || [];
      if (items.length) {
        setLines(items.map(blankLine));
      } else if (data?.grn?.lines?.length) {
        setLines(
          data.grn.lines.map((ln) => ({
            ...blankAdhocLine({ id: ln.item_id, code: ln.item_code, name: ln.item_name, unit: ln.unit, category: ln.category }),
            billed_qty: String(ln.billed_qty ?? ln.received_qty ?? ""),
            accepted_qty: String(ln.accepted_qty ?? ""),
            rejected_qty: String(ln.rejected_qty ?? "0"),
            rejection_reason: ln.rejection_reason || "",
            storage_location: ln.location || "MAIN_STORE",
          })),
        );
      } else {
        setLines([]);
      }
      setMeta({ supplier_invoice_no: "", supplier_invoice_date: "", remarks: "" });
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    const raw = searchParams.get("gate_pass");
    if (!raw || loading) return;
    const gatePassId = Number(raw);
    if (!gatePassId) return;
    openSheet({ gate_pass_id: gatePassId });
    const next = new URLSearchParams(searchParams);
    next.delete("gate_pass");
    setSearchParams(next, { replace: true });
  }, [searchParams, loading, openSheet, setSearchParams]);

  const closeSheet = () => {
    setSelectedId(null);
    setSheet(null);
    setLines([]);
    setPoPick("");
  };

  const linkPo = async (poId) => {
    if (!selectedId || !poId) return;
    setBusy(true);
    try {
      const { data } = await api.post("/store/grn/link", {
        gate_pass_id: selectedId,
        po_id: Number(poId),
      });
      setSheet(data);
      setPoPick(String(data?.po_id || poId));
      setLines((data?.purchase_order?.items || []).map(blankLine));
      toast.success(`Linked ${data.po_number || "PO"}`);
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  const updateLine = (idx, patch) => {
    setLines((prev) => prev.map((ln, i) => (i === idx ? { ...ln, ...patch } : ln)));
  };

  const totals = useMemo(() => {
    let billed = 0;
    let accepted = 0;
    let rejected = 0;
    let items = 0;
    for (const ln of lines) {
      const b = num(ln.billed_qty);
      const a = num(ln.accepted_qty);
      const r = num(ln.rejected_qty);
      if (b > 0 || a > 0 || r > 0) items += 1;
      billed += b;
      accepted += a;
      rejected += r;
    }
    return { billed, accepted, rejected, items };
  }, [lines]);

  const addAdhocLine = (itemId) => {
    const item = (sheet?.catalog_items || []).find((x) => String(x.id) === String(itemId));
    if (!item) return;
    if (lines.some((ln) => String(ln.item_id) === String(item.id))) {
      return toast.error("Item already on this GRN");
    }
    setLines((prev) => [...prev, blankAdhocLine(item)]);
    setAdhocPick("");
  };

  const removeLine = (idx) => {
    setLines((prev) => prev.filter((_, i) => i !== idx));
  };

  const submit = async () => {
    if (!isAdhoc && !sheet?.po_id && !poPick) return toast.error("Link a purchase order first");
    const payloadLines = lines
      .filter((ln) => num(ln.billed_qty) > 0 || num(ln.accepted_qty) > 0)
      .map((ln) => ({
        po_item_id: ln.po_item_id,
        item_id: ln.item_id,
        billed_qty: num(ln.billed_qty),
        received_qty: num(ln.billed_qty),
        accepted_qty: num(ln.accepted_qty),
        rejected_qty: num(ln.rejected_qty),
        rejection_reason: ln.rejection_reason || "",
        storage_location: ln.storage_location || "MAIN_STORE",
        serial_numbers: String(ln.serial_numbers || "")
          .split(/[\n,]+/)
          .map((s) => s.trim())
          .filter(Boolean),
      }));
    if (!payloadLines.length) return toast.error("Enter billed / accepted quantities");
    for (const ln of payloadLines) {
      if (ln.rejected_qty > 0 && !ln.rejection_reason) {
        return toast.error("Select a rejection reason for rejected qty");
      }
    }
    setBusy(true);
    try {
      const endpoint = isAdhoc ? "/store/grn/submit-adhoc" : "/store/grn/submit";
      const body = isAdhoc
        ? { gate_pass_id: selectedId, ...meta, lines: payloadLines }
        : {
            gate_pass_id: selectedId,
            po_id: Number(sheet.po_id || poPick),
            ...meta,
            lines: payloadLines,
          };
      const { data } = await api.post(endpoint, body);
      toast.success(data?.message || `GRN ${data?.grn?.grn_number} posted`);
      closeSheet();
      await loadQueue();
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5 sm:space-y-6" data-testid="store-grn-page">
      <PageHeader
        eyebrow="Store · Inspection"
        title="Store keeper GRN"
        description="Inspect packing material, spares, consumables and fixed assets. With a PO, match billed qty; without a PO, post stock now and procurement links rates later."
        actions={
          <Button variant="outline" size="sm" onClick={loadQueue} disabled={loading}>
            <RefreshCw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        }
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <KpiTile label="Trucks waiting" value={queue.length} sub="Awaiting store GRN" accent testId="store-grn-kpi-queue" />
        <KpiTile label="In sheet" value={totals.items} sub="Lines with qty" testId="store-grn-kpi-lines" />
        <KpiTile label="Accepted" value={totals.accepted} sub={`${totals.rejected} rejected`} testId="store-grn-kpi-accepted" />
      </div>

      {loading && !queue.length ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-10 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading yard queue…
        </div>
      ) : queue.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
          No store inward trucks waiting. Guard issues an INWARD STORE pass (PO optional), then the truck appears here after (optional) gross weigh.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="store-grn-queue">
          {queue.map((row) => (
            <button
              key={row.gate_pass_id}
              type="button"
              onClick={() => openSheet(row)}
              className="text-left rounded-2xl border border-border bg-card p-4 sm:p-5 shadow-sm hover:border-primary/40 active:scale-[0.99] transition-transform min-h-[132px] touch-manipulation"
              data-testid={`store-grn-card-${row.gate_pass_id}`}
            >
              <div className="flex items-start gap-3">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <Truck className="h-5 w-5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground tracking-wide">{row.truck_no || "—"}</span>
                    <Pill tone={typeTone(row.procurement_type)}>{row.procurement_type_label || "Store"}</Pill>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {row.driver_name || "—"} · GP {row.gate_pass_no || "—"}
                  </p>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                <div>
                  <dt className="text-muted-foreground uppercase tracking-widest text-[10px]">PO</dt>
                  <dd className="text-foreground font-medium truncate">{row.po_number || (row.po_linkage_pending ? "PO pending" : "Link PO")}</dd>
                </div>
                <div>
                  <dt className="text-muted-foreground uppercase tracking-widest text-[10px]">Supplier</dt>
                  <dd className="text-foreground truncate">{row.supplier_name || row.party_name || "—"}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-muted-foreground uppercase tracking-widest text-[10px]">Gross weight</dt>
                  <dd className="text-foreground tabular">{formatWeightKg(row.gross_weight_qtl) || "Not weighed"}</dd>
                </div>
              </dl>
            </button>
          ))}
        </div>
      )}

      {selectedId && sheet && (
        <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-card text-foreground border border-border rounded-t-3xl sm:rounded-2xl w-full max-w-5xl max-h-[96vh] overflow-hidden flex flex-col shadow-xl">
            <div className="px-4 sm:px-5 py-4 border-b border-border flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.28em] text-primary">GRN entry sheet</p>
                <h2 className="font-serif text-xl text-foreground mt-0.5 truncate">
                  {sheet.truck_no} · {sheet.gate_pass_no}
                </h2>
                <p className="text-xs text-muted-foreground mt-1">
                  {sheet.driver_name} · {sheet.supplier_name || "—"} · {formatWeightKg(sheet.gross_weight_qtl) || "Gross pending"}
                </p>
              </div>
              <Button type="button" variant="ghost" size="icon" onClick={closeSheet} aria-label="Close">
                <X className="h-5 w-5" />
              </Button>
            </div>

            <div className="overflow-y-auto px-4 sm:px-5 py-4 space-y-4 flex-1">
              {isAdhoc && (
                <div className="rounded-xl border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-amber-900 dark:text-amber-200">
                  Ad-hoc receipt — stock will post immediately. Procurement will link the purchase order for valuation later.
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {!isAdhoc && (
                <Field label="Purchase order" className="sm:col-span-1">
                  <Select
                    value={poPick || NONE}
                    onValueChange={(v) => {
                      const next = v === NONE ? "" : v;
                      setPoPick(next);
                      if (next && String(next) !== String(sheet.po_id || "")) linkPo(next);
                    }}
                  >
                    <SelectTrigger className="min-h-11 bg-card">
                      <SelectValue placeholder="Select PO…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NONE}>Select PO…</SelectItem>
                      {(sheet.open_pos || []).map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.po_number} · {p.supplier_name} · {p.category?.replaceAll("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                )}
                {isAdhoc && (
                  <Field label="Add item" className="sm:col-span-1">
                    <Select
                      value={adhocPick || NONE}
                      onValueChange={(v) => {
                        if (v !== NONE) addAdhocLine(v);
                      }}
                    >
                      <SelectTrigger className="min-h-11 bg-card">
                        <SelectValue placeholder="Pick catalog item…" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NONE}>Pick catalog item…</SelectItem>
                        {(sheet.catalog_items || []).map((it) => (
                          <SelectItem key={it.id} value={String(it.id)}>
                            {it.code} · {it.name} · {(it.category || "").replaceAll("_", " ")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                )}
                <Field label="Supplier invoice no.">
                  <Input
                    className="min-h-11"
                    value={meta.supplier_invoice_no}
                    onChange={(e) => setMeta((m) => ({ ...m, supplier_invoice_no: e.target.value }))}
                  />
                </Field>
                <Field label="Invoice date">
                  <Input
                    type="date"
                    className="min-h-11"
                    value={meta.supplier_invoice_date}
                    onChange={(e) => setMeta((m) => ({ ...m, supplier_invoice_date: e.target.value }))}
                  />
                </Field>
              </div>

              {lines.length === 0 ? (
                <p className="text-sm text-muted-foreground rounded-xl border border-dashed border-border px-4 py-8 text-center">
                  {isAdhoc ? "Add items from the catalog to receive into stock." : "Link an approved PO to load line items."}
                </p>
              ) : (
                <div className="space-y-3">
                  {lines.map((ln, idx) => (
                    <div key={ln.po_item_id || `${ln.item_id}-${idx}`} className="rounded-xl border border-border bg-card p-3 sm:p-4 space-y-3">
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="font-medium text-foreground truncate">{ln.item_name}</div>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {ln.item_code}
                            {!isAdhoc && ` · Ordered ${ln.ordered_qty} ${ln.unit} · Pending ${ln.pending_qty} ${ln.unit}`}
                            {isAdhoc && ln.unit ? ` · ${ln.unit}` : ""}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          <Pill tone="sage">{routeLabel(sheet.stock_route, ln.category || sheet.procurement_type)}</Pill>
                          {isAdhoc ? (
                            <Button type="button" variant="ghost" size="icon" onClick={() => removeLine(idx)} aria-label="Remove line">
                              <X className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
                        <Field label="Billed qty">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="min-h-11"
                            value={ln.billed_qty}
                            onChange={(e) => updateLine(idx, { billed_qty: e.target.value, accepted_qty: e.target.value })}
                          />
                        </Field>
                        <Field label="Accepted">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="min-h-11"
                            value={ln.accepted_qty}
                            onChange={(e) => updateLine(idx, { accepted_qty: e.target.value })}
                          />
                        </Field>
                        <Field label="Rejected / damaged">
                          <Input
                            type="number"
                            inputMode="decimal"
                            className="min-h-11"
                            value={ln.rejected_qty}
                            onChange={(e) => updateLine(idx, { rejected_qty: e.target.value })}
                          />
                        </Field>
                        <Field label="Rejection reason">
                          <Select
                            value={ln.rejection_reason || NONE}
                            onValueChange={(v) => updateLine(idx, { rejection_reason: v === NONE ? "" : v })}
                          >
                            <SelectTrigger className="min-h-11 bg-card">
                              <SelectValue placeholder="—" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value={NONE}>—</SelectItem>
                              {reasons.map((r) => (
                                <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                        <Field label="Rack / location">
                          <Input
                            className="min-h-11"
                            value={ln.storage_location}
                            onChange={(e) => updateLine(idx, { storage_location: e.target.value })}
                            placeholder="MAIN_STORE"
                          />
                        </Field>
                      </div>
                      {(sheet.procurement_type === "FIXED_ASSET" || ln.category === "FIXED_ASSET") && (
                        <Field label="Serial / asset tags">
                          <Input
                            className="min-h-11"
                            value={ln.serial_numbers}
                            onChange={(e) => updateLine(idx, { serial_numbers: e.target.value })}
                            placeholder="One serial per line or comma-separated"
                          />
                        </Field>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="border-t border-border px-4 sm:px-5 py-3 bg-card space-y-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
              <div className="grid grid-cols-3 gap-2 text-center">
                <SummaryChip label="Received items" value={totals.items} />
                <SummaryChip label="Accepted" value={totals.accepted} tone="ok" />
                <SummaryChip label="Rejected" value={totals.rejected} tone="warn" />
              </div>
              <Button
                type="button"
                className="w-full min-h-12 text-base bg-bansal-sage hover:bg-bansal-sageDark text-white"
                onClick={submit}
                disabled={busy || !lines.length}
                data-testid="store-grn-submit"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ClipboardCheck className="h-4 w-4 mr-2" />}
                Approve & send to tare weighbridge
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function SummaryChip({ label, value, tone }) {
  const color =
    tone === "ok"
      ? "text-emerald-700 dark:text-emerald-400"
      : tone === "warn"
        ? "text-amber-700 dark:text-amber-400"
        : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card px-2 py-2">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`font-serif text-xl tabular ${color}`}>{value}</div>
    </div>
  );
}
